import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Les trois modules, tels que la société les enregistre. */
export const MODULES = {
  batiment: ['🏗️', 'Bâtiment', 'Devis, chantiers, équipe & paie.'],
  magasin: ['🛒', 'Magasin', 'Ventes, stock, offres, remises.'],
  mixte: ['🔗', 'Mix', 'Deux sociétés séparées, sous un seul abonnement.'],
};

/**
 * Le secteur d'activité de la société, dessiné comme la maquette V288 : les
 * trois tuiles Bâtiment, Magasin et Mix, celle du module actuel en violet.
 *
 * Les tuiles sont en lecture seule : le module fixe la grille tarifaire, si
 * bien qu'on n'en change que par la page Abonnement, qui annonce le nouveau
 * prix et demande confirmation. Le lien « Changer de module » y mène.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc société
 * @param {(route: string) => void} [props.onNav] navigation vers l'abonnement
 * @returns {import('react').ReactElement} la carte
 */
export function SecteurCard({ soc, onNav }) {
  // « chantier » est un ancien mot du bâtiment : une société sans module
  // lisible reste présentée comme bâtiment.
  // Le Mix, ce sont deux sociétés : chacune porte son module (bâtiment ou
  // magasin), et c'est la société liée qui dit que l'abonnement est un Mix.
  const soeur = soc?.abonnement?.societeLiee;
  const actuel = soeur ? 'mixte' : MODULES[soc?.secteur] ? soc.secteur : 'batiment';
  const ici = soc?.secteur === 'magasin' ? 'Magasin' : 'Bâtiment';
  const autre = soeur?.secteur === 'magasin' ? 'Magasin' : 'Bâtiment';
  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="🔀"
        title="Secteur d'activité"
        color={C.purple}
        sub="Fixé par ton abonnement — tes données sont conservées"
      />
      <div style={{ padding: 13 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.entries(MODULES).map(([v, [ic, lb]]) => {
            const on = actuel === v;
            return (
              <div
                key={v}
                aria-current={on ? 'true' : undefined}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '12px 6px',
                  borderRadius: 11,
                  background: on ? C.purple : C.navyXL,
                  color: on ? '#fff' : C.ink,
                  border: '1px solid ' + (on ? C.purple : C.border),
                }}
              >
                <div style={{ fontSize: 20 }}>{ic}</div>
                <div style={{ fontSize: 10.5, marginTop: 3, fontWeight: 800 }}>{lb}</div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
          {/* Seul le module souscrit est décrit : les autres n'ont rien à ouvrir ici. */}
          {MODULES[actuel][1]} : {MODULES[actuel][2]}
          {soeur ? (
            <div data-testid="mix-deux-societes" style={{ marginTop: 4 }}>
              {trf(
                'Cette société : {0}. L’autre société : {1}. Chacune a ses coordonnées, ses clients, ses documents et sa comptabilité ; le bouton en haut de l’écran passe de l’une à l’autre.',
                `${soc?.nom || '—'} (${ici})`,
                `${soeur.nom || '—'} (${autre})`,
              )}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onNav && onNav('abonnement')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginTop: 8,
            color: C.purple,
            fontSize: 10,
            fontWeight: 800,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Changer de module
        </button>
      </div>
    </Card>
  );
}
